import { Icon } from '@iconify/react';
import React, { useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { formatMoneyUSD } from '../../utils/functions';
import useDataStore from '../../../../common/src/stores/useDataStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { TH_BASE_CLASS, TableExportRef } from '../../utils/table-styles';
import { exportTableToExcel } from '../../utils/export-to-excel';

interface Props {
  settlements: Settlement[];
  kgOrBox: 'kg' | 'box';
  costValueMode: 'average' | 'total';
}

interface CostRow {
  label: string;
  isTotal?: boolean;
  comisionRate: number;
  taxRate: number;
  maritimeFlete: number;
  maritimeRepacking: number;
  maritimoOtros: number;
  aereoFlete: number;
  aereoRepacking: number;
  aereoOtros: number;
  terrestreFlete: number;
  terrestreRepacking: number;
  terrestreOtros: number;
}

type SortKey =
  | 'label'
  | 'comisionRate'
  | 'taxRate'
  | 'maritimeFlete'
  | 'maritimeRepacking'
  | 'maritimoOtros'
  | 'aereoFlete'
  | 'aereoRepacking'
  | 'aereoOtros'
  | 'terrestreFlete'
  | 'terrestreRepacking'
  | 'terrestreOtros';

const ClientCostsTable = forwardRef<TableExportRef, Props>(({ settlements, kgOrBox, costValueMode }, ref) => {
  const { selectedSpecies } = useDataStore();
  const { t } = useTranslation('settlements');
  const [sortKey, setSortKey] = useState<SortKey>('comisionRate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Totals row is always visible; data rows paginate via visibleCount.
  const [visibleCount, setVisibleCount] = useState(10);
  const boxSize = selectedSpecies?.default_unit_price || 5;
  const multiplier = kgOrBox === 'box' ? boxSize : 1;

  const { totalRow, clientRows } = useMemo(() => {
    if (!settlements || settlements.length === 0) return { totalRow: null, clientRows: [] };

    const accumulate = (subset: Settlement[]) => {
      let totalRevenueUSD = 0;
      let totalCommissionUSD = 0;
      let totalTaxUSD = 0;
      let maritimeFleteUSD = 0, maritimoOtrosUSD = 0, maritimeKilos = 0;
      let maritimeRepackingUSD = 0;
      let aereoFleteUSD = 0, aereoOtrosUSD = 0, aereoKilos = 0;
      let aereoRepackingUSD = 0;
      let terrestreFleteUSD = 0, terrestreOtrosUSD = 0, terrestreKilos = 0;
      let terrestreRepackingUSD = 0;

      subset.forEach((s) => {
        const exchangeRate = s.exchange_rate || 1;
        const shipType = s.ship_trip?.ship?.type;
        const kilos = s.settlement_details.reduce((sum, d) => sum + (d.kilos || 0), 0);

        totalCommissionUSD += (s.total_commission_local || 0) / exchangeRate;
        totalTaxUSD += (s.total_tax_local || 0) / exchangeRate;
        totalRevenueUSD += (s.total_revenue_local || 0) / exchangeRate;

        const freightUSD = s.total_freight_usd || 0;
        const otherCostsUSD = (s.total_other_costs_local || 0) / exchangeRate;
        const repackingUSD =
          (s.total_repacking_costs_local || 0) / exchangeRate;

        if (kilos > 0) {
          if (shipType === 'A') {
            aereoFleteUSD += freightUSD; aereoRepackingUSD += repackingUSD; aereoOtrosUSD += otherCostsUSD; aereoKilos += kilos;
          } else if (shipType === 'T') {
            terrestreFleteUSD += freightUSD; terrestreRepackingUSD += repackingUSD; terrestreOtrosUSD += otherCostsUSD; terrestreKilos += kilos;
          } else {
            maritimeFleteUSD += freightUSD; maritimeRepackingUSD += repackingUSD; maritimoOtrosUSD += otherCostsUSD; maritimeKilos += kilos;
          }
        }
      });

      if (costValueMode === 'total') {
        return {
          comisionRate: totalCommissionUSD,
          taxRate: totalTaxUSD,
          maritimeFlete: maritimeFleteUSD,
          maritimeRepacking: maritimeRepackingUSD,
          maritimoOtros: maritimoOtrosUSD,
          aereoFlete: aereoFleteUSD,
          aereoRepacking: aereoRepackingUSD,
          aereoOtros: aereoOtrosUSD,
          terrestreFlete: terrestreFleteUSD,
          terrestreRepacking: terrestreRepackingUSD,
          terrestreOtros: terrestreOtrosUSD,
        };
      }

      return {
        comisionRate: totalRevenueUSD > 0 ? totalCommissionUSD / totalRevenueUSD : 0,
        taxRate: totalRevenueUSD > 0 ? totalTaxUSD / totalRevenueUSD : 0,
        maritimeFlete: maritimeKilos > 0 ? (maritimeFleteUSD / maritimeKilos) * multiplier : 0,
        maritimeRepacking: maritimeKilos > 0 ? (maritimeRepackingUSD / maritimeKilos) * multiplier : 0,
        maritimoOtros: maritimeKilos > 0 ? (maritimoOtrosUSD / maritimeKilos) * multiplier : 0,
        aereoFlete: aereoKilos > 0 ? (aereoFleteUSD / aereoKilos) * multiplier : 0,
        aereoRepacking: aereoKilos > 0 ? (aereoRepackingUSD / aereoKilos) * multiplier : 0,
        aereoOtros: aereoKilos > 0 ? (aereoOtrosUSD / aereoKilos) * multiplier : 0,
        terrestreFlete: terrestreKilos > 0 ? (terrestreFleteUSD / terrestreKilos) * multiplier : 0,
        terrestreRepacking: terrestreKilos > 0 ? (terrestreRepackingUSD / terrestreKilos) * multiplier : 0,
        terrestreOtros: terrestreKilos > 0 ? (terrestreOtrosUSD / terrestreKilos) * multiplier : 0,
      };
    };

    const tr: CostRow = {
      label: costValueMode === 'total' ? t('TOTAL') : t('PROMEDIO'),
      isTotal: true,
      ...accumulate(settlements),
    };

    const byClient: Record<string, Settlement[]> = {};
    settlements.forEach((s) => {
      if (!byClient[s.receiver]) byClient[s.receiver] = [];
      byClient[s.receiver].push(s);
    });
    const cr: CostRow[] = Object.entries(byClient)
      .map(([client, subs]) => ({ label: client, ...accumulate(subs) }));

    return { totalRow: tr, clientRows: cr };
  }, [settlements, multiplier, t, costValueMode]);

  const sortedClientRows = useMemo(() => {
    return [...clientRows].sort((a, b) => {
      let cmp: number;
      if (sortKey === 'label') {
        cmp = a.label.localeCompare(b.label);
      } else {
        cmp = a[sortKey] - b[sortKey];
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [clientRows, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'label' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey === column) {
      return sortDir === 'asc' ? (
        <Icon icon='tabler:chevron-up' className='inline ml-0.5' width={10} />
      ) : (
        <Icon icon='tabler:chevron-down' className='inline ml-0.5' width={10} />
      );
    }
    return <Icon icon='tabler:chevron-down' className='inline ml-0.5 opacity-0 group-hover/th:opacity-40' width={10} />;
  };

  const unitLabel = kgOrBox === 'kg' ? 'KG' : `CEQ ${boxSize}KG`;
  const fmtRate = (v: number) =>
    costValueMode === 'total' ? formatMoneyUSD(v, { decimals: 0 }) : `${(v * 100).toFixed(1)}%`;
  const fmtCost = (v: number) =>
    v > 0 ? formatMoneyUSD(v, { decimals: costValueMode === 'total' ? 0 : 2 }) : '—';
  const colLabel = (base: string) =>
    costValueMode === 'total' ? t(base) : `${t(base)} / ${unitLabel}`;
  const showMaritimeRepacking = (totalRow?.maritimeRepacking || 0) > 0;
  const showAirRepacking = (totalRow?.aereoRepacking || 0) > 0;
  const showGroundRepacking = (totalRow?.terrestreRepacking || 0) > 0;
  const maritimeColSpan = showMaritimeRepacking ? 3 : 2;
  const airColSpan = showAirRepacking ? 3 : 2;
  const groundColSpan = showGroundRepacking ? 3 : 2;
  const minTableWidth =
    900 +
    [showMaritimeRepacking, showAirRepacking, showGroundRepacking].filter(Boolean)
      .length *
      120;

  const handleExport = () => {
    const allRows = totalRow ? [totalRow, ...sortedClientRows] : sortedClientRows;
    const headers = [
      t('Cliente'),
      t('Comisión'),
      t('Impuestos'),
      `${t('Flete')} ${t('Marítimo')}`,
      ...(showMaritimeRepacking ? [`${t('Repacking')} ${t('Marítimo')}`] : []),
      `${t('Otros')} ${t('Marítimo')}`,
      `${t('Flete')} ${t('Aéreo')}`,
      ...(showAirRepacking ? [`${t('Repacking')} ${t('Aéreo')}`] : []),
      `${t('Otros')} ${t('Aéreo')}`,
      `${t('Flete')} ${t('Terrestre')}`,
      ...(showGroundRepacking ? [`${t('Repacking')} ${t('Terrestre')}`] : []),
      `${t('Otros')} ${t('Terrestre')}`,
    ];

    exportTableToExcel({
      fileName: 'Costos_por_Cliente',
      sheetName: t('Costos'),
      headers,
      rows: allRows.map((row) => [
        row.label,
        fmtRate(row.comisionRate),
        fmtRate(row.taxRate),
        fmtCost(row.maritimeFlete),
        ...(showMaritimeRepacking ? [fmtCost(row.maritimeRepacking)] : []),
        fmtCost(row.maritimoOtros),
        fmtCost(row.aereoFlete),
        ...(showAirRepacking ? [fmtCost(row.aereoRepacking)] : []),
        fmtCost(row.aereoOtros),
        fmtCost(row.terrestreFlete),
        ...(showGroundRepacking ? [fmtCost(row.terrestreRepacking)] : []),
        fmtCost(row.terrestreOtros),
      ]),
    });
  };

  useImperativeHandle(ref, () => ({ exportToExcel: handleExport }), [handleExport]);

  if (!totalRow) return null;

  const renderRow = (row: CostRow) => (
    <tr
      key={row.label}
      className={row.isTotal
        ? 'bg-default-100 font-semibold border-b-2 border-default-200'
        : 'border-b border-default-100 hover:bg-default-50'}
    >
      <td className='py-2 px-3 font-medium'>{row.label}</td>
      <td className='py-2 px-3 text-right'>{fmtRate(row.comisionRate)}</td>
      <td className='py-2 px-3 text-right'>{fmtRate(row.taxRate)}</td>
      <td className='py-2 px-3 text-right'>{fmtCost(row.maritimeFlete)}</td>
      {showMaritimeRepacking && (
        <td className='py-2 px-3 text-right'>
          {fmtCost(row.maritimeRepacking)}
        </td>
      )}
      <td className='py-2 px-3 text-right'>{fmtCost(row.maritimoOtros)}</td>
      <td className='py-2 px-3 text-right'>{fmtCost(row.aereoFlete)}</td>
      {showAirRepacking && (
        <td className='py-2 px-3 text-right'>{fmtCost(row.aereoRepacking)}</td>
      )}
      <td className='py-2 px-3 text-right'>{fmtCost(row.aereoOtros)}</td>
      <td className='py-2 px-3 text-right'>{fmtCost(row.terrestreFlete)}</td>
      {showGroundRepacking && (
        <td className='py-2 px-3 text-right'>
          {fmtCost(row.terrestreRepacking)}
        </td>
      )}
      <td className='py-2 px-3 text-right'>{fmtCost(row.terrestreOtros)}</td>
    </tr>
  );

  return (
    <div>
      <div className='overflow-x-auto'>
      <table className='w-full text-sm' style={{ minWidth: `${minTableWidth}px` }}>
        <thead>
          <tr>
            <th rowSpan={2} className={`${TH_BASE_CLASS} bg-default-100 text-left rounded-tl-md`} style={{ width: '12%' }} onClick={() => handleSort('label')}>
              {t('CLIENTE')}
              <SortIcon column='label' />
            </th>
            <th colSpan={2} className='bg-default-100 text-center text-default-400 text-[11px] font-semibold py-1.5 border-b border-default-200'>
              {t('COSTOS VARIABLES')}
            </th>
            <th colSpan={maritimeColSpan} className='bg-default-100 text-center text-default-400 text-[11px] font-semibold py-1.5 border-b border-default-200 border-l border-default-200'>
              {t('MARÍTIMOS')}
            </th>
            <th colSpan={airColSpan} className='bg-default-100 text-center text-default-400 text-[11px] font-semibold py-1.5 border-b border-default-200 border-l border-default-200'>
              {t('AÉREOS')}
            </th>
            <th colSpan={groundColSpan} className='bg-default-100 text-center text-default-400 text-[11px] font-semibold py-1.5 border-b border-default-200 border-l border-default-200 rounded-tr-md'>
              {t('TERRESTRES')}
            </th>
          </tr>
          <tr>
            <th className={`${TH_BASE_CLASS} bg-default-100 text-right`} onClick={() => handleSort('comisionRate')}>
              {t('COMISIÓN')}<SortIcon column='comisionRate' />
            </th>
            <th className={`${TH_BASE_CLASS} bg-default-100 text-right`} onClick={() => handleSort('taxRate')}>
              {t('IMPUESTOS')}<SortIcon column='taxRate' />
            </th>
            <th className={`${TH_BASE_CLASS} bg-default-100 text-right border-l border-default-200`} onClick={() => handleSort('maritimeFlete')}>
              {colLabel('FLETE')}<SortIcon column='maritimeFlete' />
            </th>
            {showMaritimeRepacking && (
              <th className={`${TH_BASE_CLASS} bg-default-100 text-right`} onClick={() => handleSort('maritimeRepacking')}>
                {colLabel('REPACKING')}<SortIcon column='maritimeRepacking' />
              </th>
            )}
            <th className={`${TH_BASE_CLASS} bg-default-100 text-right`} onClick={() => handleSort('maritimoOtros')}>
              {colLabel('OTROS')}<SortIcon column='maritimoOtros' />
            </th>
            <th className={`${TH_BASE_CLASS} bg-default-100 text-right border-l border-default-200`} onClick={() => handleSort('aereoFlete')}>
              {colLabel('FLETE')}<SortIcon column='aereoFlete' />
            </th>
            {showAirRepacking && (
              <th className={`${TH_BASE_CLASS} bg-default-100 text-right`} onClick={() => handleSort('aereoRepacking')}>
                {colLabel('REPACKING')}<SortIcon column='aereoRepacking' />
              </th>
            )}
            <th className={`${TH_BASE_CLASS} bg-default-100 text-right`} onClick={() => handleSort('aereoOtros')}>
              {colLabel('OTROS')}<SortIcon column='aereoOtros' />
            </th>
            <th className={`${TH_BASE_CLASS} bg-default-100 text-right border-l border-default-200`} onClick={() => handleSort('terrestreFlete')}>
              {colLabel('FLETE')}<SortIcon column='terrestreFlete' />
            </th>
            {showGroundRepacking && (
              <th className={`${TH_BASE_CLASS} bg-default-100 text-right`} onClick={() => handleSort('terrestreRepacking')}>
                {colLabel('REPACKING')}<SortIcon column='terrestreRepacking' />
              </th>
            )}
            <th className={`${TH_BASE_CLASS} bg-default-100 text-right`} onClick={() => handleSort('terrestreOtros')}>
              {colLabel('OTROS')}<SortIcon column='terrestreOtros' />
            </th>
          </tr>
        </thead>
        <tbody>
          {renderRow(totalRow)}
          {sortedClientRows.slice(0, visibleCount).map(renderRow)}
        </tbody>
      </table>
    </div>
    {sortedClientRows.length > visibleCount && (
      <div className='flex justify-center mt-2'>
        <Button
          variant='light'
          size='sm'
          onPress={() =>
            setVisibleCount((c) =>
              Math.min(c + 10, sortedClientRows.length)
            )
          }
          className='w-full'
        >
          {t('Cargar más')} ({sortedClientRows.length - visibleCount}{' '}
          {t('restantes')})
        </Button>
      </div>
    )}
    </div>
  );
});

export default React.memo(ClientCostsTable);
